/**
 * "Still want to make this?" — the check-in for recipes she saved and left.
 *
 * Which recipes are waiting comes from shared/nudge.ts, the same function the
 * server uses to decide who gets a reminder, so this card and the push always
 * agree. The dates offered here are a convenience; the server refuses any it
 * would not accept.
 */
import { useState } from 'react'
import Icon from '../Icon'
import config from '../../../studio42.config.json'
import { businessDate, businessDatePlus, businessNow } from '../../../shared/time'
import { PLAN_HORIZON_DAYS, nudgeState, type NudgeState } from '../../../shared/nudge'
import { checkIn, recordPastMake, type RecipeRow } from '../../lib/savenmake'
import { confetti, sound } from '../../lib/fun'

const TZ = config.timezone

/** "Saturday, Oct 4", "today" or "tomorrow" — for a YYYY-MM-DD business date. */
export function dayLabel(date: string, now = Date.now()): string {
  if (date === businessDate(TZ, now)) return 'today'
  if (date === businessDatePlus(TZ, 1, now)) return 'tomorrow'
  // Noon UTC on that date, formatted in UTC, is that calendar day everywhere.
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' })
    .format(Date.parse(`${date}T12:00:00Z`))
}

/** The next Saturday, or today if today is one. */
function weekend(now: number): string {
  const { weekday } = businessNow(TZ, now)
  return businessDatePlus(TZ, (6 - weekday + 7) % 7, now)
}

/**
 * The answers to one check-in. Used on the card and on the recipe itself,
 * because a reminder about one recipe opens that recipe.
 */
export function CheckIn({ recipe, state, onChanged }: {
  recipe: RecipeRow
  state: NudgeState
  onChanged: () => void
}) {
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const now = Date.now()

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setPicking(false)
      onChanged()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  // Sounds are played in the tap itself, before any await — iOS only allows audio there.
  const plan = (date: string) => {
    sound('planned')
    return act(async () => { await checkIn(recipe.id, { action: 'plan', date }); confetti(30) })
  }

  if (picking) {
    const today = businessDate(TZ, now)
    const sat = weekend(now)
    return (
      <div className="filter-row">
        <button type="button" disabled={busy} onClick={() => plan(today)}>Tonight</button>
        <button type="button" disabled={busy} onClick={() => plan(businessDatePlus(TZ, 1, now))}>Tomorrow</button>
        {sat !== today && <button type="button" disabled={busy} onClick={() => plan(sat)}>This weekend</button>}
        <input
          type="date" aria-label="Pick a day" style={{ width: 'auto' }}
          min={today} max={businessDatePlus(TZ, PLAN_HORIZON_DAYS, now)}
          onChange={(e) => { if (e.target.value) void plan(e.target.value) }}
        />
        <button type="button" className="link" onClick={() => setPicking(false)}>Cancel</button>
        {error && <p className="error small" role="alert">{error}</p>}
      </div>
    )
  }

  return (
    <div className="filter-row">
      <button type="button" className="primary" style={{ width: 'auto' }} disabled={busy} onClick={() => setPicking(true)}>
        <Icon name="calendar" size={15} /> {state?.kind === 'planned' ? 'Move it' : state?.kind === 'plan_passed' ? 'Another day' : 'Plan it'}
      </button>
      <button type="button" disabled={busy} onClick={() => { sound('cooked'); void act(() => recordPastMake({ recipeId: recipe.id })) }}>
        <Icon name="check" size={15} /> I made it
      </button>
      {state?.kind === 'planned'
        ? <button type="button" disabled={busy} onClick={() => act(() => checkIn(recipe.id, { action: 'unplan' }))}>Unplan</button>
        : <button type="button" disabled={busy} onClick={() => act(() => checkIn(recipe.id, { action: 'later' }))}>
            <Icon name="clock" size={15} /> Next week
          </button>}
      {state?.kind !== 'planned' && (
        <button type="button" className="link" disabled={busy} onClick={() => act(() => checkIn(recipe.id, { action: 'stop' }))}>
          Stop asking
        </button>
      )}
      {error && <p className="error small" role="alert">{error}</p>}
    </div>
  )
}

/** One line saying why this recipe is here. */
export function whyWaiting(state: NudgeState): string {
  if (!state) return ''
  if (state.kind === 'planned') return `Planned for ${dayLabel(state.plannedFor)}`
  if (state.kind === 'plan_passed') return `Planned for ${dayLabel(state.plannedFor)} — did you make it?`
  return `Saved ${state.idleDays} days ago and not made yet`
}

/**
 * The card at the top of Discover: what is planned, and what has been
 * sitting. Renders nothing when there is neither.
 */
export default function Waiting({ recipes, onChanged, onOpen }: {
  recipes: RecipeRow[]
  onChanged: () => void
  onOpen: (r: RecipeRow) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const now = Date.now()

  const rows = recipes
    .map((r) => ({ r, state: nudgeState(r, TZ, now) }))
    .filter((x): x is { r: RecipeRow; state: NonNullable<NudgeState> } => x.state !== null)

  const coming = rows.filter((x) => x.state.kind === 'planned')
    .sort((a, b) => (a.r.plannedFor ?? '').localeCompare(b.r.plannedFor ?? ''))
  // A passed plan first, then the longest-waiting.
  const waiting = rows.filter((x) => x.state.kind !== 'planned').sort((a, b) =>
    a.state.kind !== b.state.kind
      ? (a.state.kind === 'plan_passed' ? -1 : 1)
      : (b.state.kind === 'stale' ? b.state.idleDays : 0) - (a.state.kind === 'stale' ? a.state.idleDays : 0))

  if (!coming.length && !waiting.length) return null
  const shown = showAll ? waiting : waiting.slice(0, 3)

  return (
    <div className="outcome partial" style={{ marginBottom: 20 }}>
      {!!coming.length && (
        <>
          <h3><Icon name="calendar" size={18} /> Coming up</h3>
          {coming.map(({ r, state }) => (
            <div className="filter-row" key={r.id} style={{ marginBottom: 8 }}>
              <button type="button" className="link" onClick={() => onOpen(r)}><b>{r.title}</b></button>
              <span className="muted small">{whyWaiting(state)}</span>
            </div>
          ))}
        </>
      )}

      {!!waiting.length && (
        <>
          <h3 style={coming.length ? { marginTop: 16 } : undefined}>
            <Icon name="clock" size={18} /> Waiting on you
          </h3>
          {shown.map(({ r, state }) => (
            <div key={r.id} style={{ marginBottom: 14 }}>
              <div className="filter-row">
                <button type="button" className="link" onClick={() => onOpen(r)}><b>{r.title}</b></button>
                <span className="muted small">{whyWaiting(state)}</span>
              </div>
              <CheckIn recipe={r} state={state} onChanged={onChanged} />
            </div>
          ))}
          {waiting.length > shown.length && (
            <button type="button" className="link" onClick={() => setShowAll(true)}>
              {waiting.length - shown.length} more
            </button>
          )}
        </>
      )}
    </div>
  )
}
